/**
 * SignatureDetailDrawer — member-facing read-only detail surface
 * for a Concierge® Signature Escape.
 *
 * Founder ruling 18 Feb 2026 PM-14 (Wave B-1):
 *   Signature Escapes need a proper member drawer (not just a card +
 *   request modal). Show member_whisper, best-for, season, duration,
 *   safety / visa / age / cancellation notes, "Price on request", and
 *   the request CTA. Above all that, surface a Viator photo gallery
 *   built from the destination — gives the row the same image depth
 *   `/escape` partner products carry, without the desk having to
 *   upload 6 photos per row.
 *
 * Doctrine:
 *   - All copy shown here is already scrubbed server-side
 *     (escape_relevance.scrub_row_for_member).
 *   - Viator photos are SUPPLEMENTAL; the desk's curated
 *     `hero_image_url` still leads the drawer.
 *   - Internal fields (desk_notes, *_internal) are NEVER fetched on
 *     this endpoint.
 */
import React, { useEffect, useState } from "react";
import {
    getEscapeSignature,
    getEscapeSignatureViatorPhotos,
} from "@/lib/escape-api";
import { DrawerVoiceLine } from "@/components/shared/DrawerAtmosphere";
import { cldOpt } from "@/lib/cloudinary";

const T = {
    ink: "#211C15", ink2: "#5C5447", ink3: "#8A8071",
    line: "#E5DCCB", line2: "#D8CDB7",
    card: "#FCFAF4", paper: "#F4EFE6",
    brass: "#8B6914", brassDim: "#C6A560", brassWash: "#EFE6D3",
};

export default function SignatureDetailDrawer({
    open,
    signatureId,
    onClose,
    onRequest,
}) {
    const [row, setRow] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [photosLoading, setPhotosLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activePhoto, setActivePhoto] = useState(0);

    useEffect(() => {
        if (!open || !signatureId) {
            setRow(null);
            setPhotos([]);
            setActivePhoto(0);
            setError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        getEscapeSignature(signatureId)
            .then((r) => {
                if (!cancelled) {
                    setRow(r.row);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e?.message || "Could not load Signature Escape.");
                    setLoading(false);
                }
            });
        // Viator photo gallery fetch in parallel · failure here is
        // never fatal · the drawer still renders.
        setPhotosLoading(true);
        getEscapeSignatureViatorPhotos(signatureId, { count: 6 })
            .then((r) => {
                if (!cancelled) setPhotos(r.photos || []);
            })
            .catch(() => { if (!cancelled) setPhotos([]); })
            .finally(() => { if (!cancelled) setPhotosLoading(false); });
        return () => { cancelled = true; };
    }, [open, signatureId]);

    if (!open) return null;

    // Compose the gallery: curated hero first, then Viator photos.
    const galleryUrls = [];
    if (row?.hero_image_url) galleryUrls.push(row.hero_image_url);
    for (const p of photos) {
        if (!galleryUrls.includes(p.url)) galleryUrls.push(p.url);
    }

    return (
        <div
            data-testid="escape-signature-drawer"
            role="dialog"
            aria-label="Signature Escape detail"
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, zIndex: 50,
                background: "rgba(33,28,21,0.42)",
                display: "flex", alignItems: "stretch", justifyContent: "flex-end",
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: T.paper,
                    width: "min(820px, 100vw)",
                    height: "100%",
                    overflowY: "auto",
                    borderLeft: `1px solid ${T.line}`,
                }}
            >
                {/* Sticky close */}
                <div
                    style={{
                        position: "sticky", top: 0, zIndex: 5,
                        background: T.paper,
                        padding: "12px 18px",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        borderBottom: `1px solid ${T.line}`,
                    }}
                >
                    <span
                        className="t-mono uppercase"
                        style={{
                            fontSize: 10, letterSpacing: "0.22em",
                            color: T.brass, fontFamily: "ui-monospace, monospace",
                        }}
                    >Signature Escape · By arrangement</span>
                    <button
                        type="button"
                        data-testid="escape-signature-drawer-close"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            background: "transparent", border: 0,
                            fontSize: 26, color: T.ink3, cursor: "pointer",
                            lineHeight: 1, padding: 4,
                        }}
                    >×</button>
                </div>

                {loading && (
                    <div style={{ padding: 28 }}>
                        <div style={{
                            width: "100%", aspectRatio: "16/10",
                            background: T.line, borderRadius: 14,
                            opacity: 0.6,
                        }} />
                        <div style={{ height: 24, background: T.line, marginTop: 16, borderRadius: 6, opacity: 0.6 }} />
                        <div style={{ height: 60, background: T.line, marginTop: 12, borderRadius: 6, opacity: 0.4 }} />
                    </div>
                )}

                {error && (
                    <div style={{ padding: 28, color: T.ink2 }}>
                        <p>{error}</p>
                        <button
                            onClick={onClose}
                            style={{
                                marginTop: 12, background: T.ink, color: T.card,
                                border: 0, borderRadius: 10, padding: "8px 14px",
                                cursor: "pointer", fontSize: 13,
                            }}
                        >Close</button>
                    </div>
                )}

                {!loading && !error && row && (
                    <div style={{ padding: "20px 28px 40px" }}>
                        {/* Hero / gallery */}
                        {galleryUrls.length > 0 && (
                            <div data-testid="escape-signature-drawer-gallery">
                                <div
                                    style={{
                                        width: "100%", aspectRatio: "16/10",
                                        background: `url(${cldOpt(galleryUrls[activePhoto] || galleryUrls[0], { w: 1200 })}) center/cover no-repeat`,
                                        borderRadius: 14,
                                        border: `1px solid ${T.line}`,
                                    }}
                                />
                                {galleryUrls.length > 1 && (
                                    <div
                                        data-testid="escape-signature-drawer-thumbs"
                                        style={{
                                            display: "flex", gap: 8, marginTop: 10,
                                            overflowX: "auto", paddingBottom: 4,
                                        }}
                                    >
                                        {galleryUrls.map((u, i) => (
                                            <button
                                                key={u}
                                                type="button"
                                                data-testid={`escape-signature-drawer-thumb-${i}`}
                                                onClick={() => setActivePhoto(i)}
                                                style={{
                                                    flex: "0 0 auto",
                                                    width: 88, height: 60,
                                                    border: i === activePhoto ? `2px solid ${T.ink}` : `1px solid ${T.line2}`,
                                                    borderRadius: 8,
                                                    background: `url(${cldOpt(u, { w: 176 })}) center/cover no-repeat`,
                                                    cursor: "pointer", padding: 0,
                                                }}
                                                aria-label={`Photo ${i + 1}`}
                                            />
                                        ))}
                                    </div>
                                )}
                                {photos.length > 0 && (
                                    <p
                                        style={{
                                            fontSize: 10, color: T.ink3,
                                            marginTop: 8,
                                            letterSpacing: "0.14em",
                                            textTransform: "uppercase",
                                            fontFamily: "ui-monospace, monospace",
                                        }}
                                    >
                                        Gallery · Concierge® hero + supplemental destination photography from Viator
                                    </p>
                                )}
                                {photosLoading && photos.length === 0 && (
                                    <p style={{ fontSize: 11, color: T.ink3, marginTop: 8 }}>
                                        Loading destination photography…
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Title + location */}
                        <h2
                            className="t-serif"
                            data-testid="escape-signature-drawer-title"
                            style={{
                                fontSize: 28, color: T.ink, fontWeight: 500,
                                margin: "22px 0 6px", letterSpacing: "-0.01em",
                                lineHeight: 1.15,
                            }}
                        >{row.title}</h2>
                        <p style={{ fontSize: 13, color: T.ink3, margin: 0 }}>
                            {[row.destination, row.country].filter(Boolean).join(" · ")}
                        </p>
                        {row.region && row.region !== row.destination && (
                            <p style={{ fontSize: 12, color: T.ink3, margin: "2px 0 0" }}>
                                {row.region}
                            </p>
                        )}

                        {/* Member whisper · rendered through shared
                           DrawerVoiceLine (brass left rule + "— the
                           Concierge®" attribution) for cross-pillar
                           parity. The whisper text is desk-curated
                           server-side (escape_relevance.scrub). */}
                        {row.concierge_whisper && (
                            <div style={{ marginTop: 20 }}>
                                <DrawerVoiceLine
                                    text={row.concierge_whisper}
                                    T={T}
                                    testid="escape-signature-drawer-whisper"
                                />
                            </div>
                        )}

                        {/* Facts grid */}
                        <div
                            data-testid="escape-signature-drawer-facts"
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                                gap: 14, marginTop: 24,
                                padding: 16,
                                background: T.card,
                                border: `1px solid ${T.line}`,
                                borderRadius: 12,
                            }}
                        >
                            <Fact label="Best for" value={row.best_for} testid="fact-best-for" />
                            <Fact label="Season · best months" value={row.season_best_months} testid="fact-season" />
                            <Fact label="Duration" value={row.duration} testid="fact-duration" />
                            <Fact label="Price" value="On request" testid="fact-price" />
                        </div>

                        {/* Notes */}
                        <Section title="Safety & suitability" body={row.safety_notes} testid="section-safety" />
                        <Section title="Visa / permits" body={row.visa_or_permit_notes} testid="section-visa" />
                        <Section title="Age & fitness" body={row.age_fitness_notes} testid="section-age" />
                        <Section title="Cancellation" body={row.cancellation_notes} testid="section-cancellation" />
                        <Section title="Access route" body={row.access_route} testid="section-access" />
                        <Section title="Booking rule" body={row.booking_rule} testid="section-booking-rule" />

                        {/* Footer · provenance + CTA */}
                        <div
                            style={{
                                marginTop: 32, padding: "20px 0",
                                borderTop: `1px solid ${T.line}`,
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", flexWrap: "wrap", gap: 12,
                            }}
                        >
                            <div style={{ minWidth: 0 }}>
                                <span
                                    style={{
                                        fontSize: 10, color: T.brass,
                                        letterSpacing: "0.18em", textTransform: "uppercase",
                                        fontFamily: "ui-monospace, monospace",
                                    }}
                                >Curated by Concierge®</span>
                                {row.supplier_name && (
                                    <p style={{ fontSize: 12, color: T.ink3, margin: "4px 0 0" }}>
                                        Operator: {row.supplier_name}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                data-testid="escape-signature-drawer-request"
                                onClick={() => onRequest?.(row)}
                                style={{
                                    background: T.ink, color: T.card,
                                    border: 0, borderRadius: 14,
                                    padding: "13px 22px",
                                    fontSize: 14, fontWeight: 500,
                                    cursor: "pointer", fontFamily: "inherit",
                                }}
                            >Request via Concierge®</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Fact({ label, value, testid }) {
    if (!value) return null;
    return (
        <div data-testid={testid}>
            <div
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    textTransform: "uppercase", color: T.ink3,
                    marginBottom: 4,
                    fontFamily: "ui-monospace, monospace",
                }}
            >{label}</div>
            <div style={{ fontSize: 14, color: T.ink, lineHeight: 1.4 }}>{value}</div>
        </div>
    );
}

function Section({ title, body, testid }) {
    if (!body) return null;
    return (
        <section
            data-testid={testid}
            style={{ marginTop: 24 }}
        >
            <h4
                className="t-mono uppercase"
                style={{
                    fontSize: 11, letterSpacing: "0.22em",
                    color: T.brass,
                    margin: "0 0 8px",
                    fontFamily: "ui-monospace, monospace",
                    fontWeight: 600,
                }}
            >{title}</h4>
            <p style={{ fontSize: 14, color: T.ink2, lineHeight: 1.55, margin: 0, whiteSpace: "pre-line" }}>
                {body}
            </p>
        </section>
    );
}
